import {
  FlexBoxImageScaleView,
  FlexBoxImageScaleViewOpts,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import { ViewOpts } from '@play-co/timestep-core/ui';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import i18n from 'src/lib/i18n/i18n';
import gsap from 'gsap';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';

const assets = {
  active: 'assets/ui/shared/buttons/panel_toggle_active.png',
  inactive: 'assets/ui/shared/buttons/panel_toggle_inactive.png',
  switch: 'assets/ui/shared/buttons/panel_toggle_switch.png',
};

const labelColor = {
  active: '#FFFFFF',
  inactive: '#A04C33',
};

const labelShadowColor = {
  active: '#068D65',
  inactive: '#5A290E',
};

const baseLabelSize = 23;
const baseLabelPadding = 5;

const panelAssetDimensions = {
  width: 65,
  height: 54,
};

const switchAssetDimensions = {
  width: 57,
  height: 55,
};

const baseDimensions = {
  width: 133,
  height: 54,
};

const panelSlice = Math.floor(panelAssetDimensions.width * 0.5);
const panelConfig: FlexBoxImageScaleViewOpts = {
  scaleMethod: '3slice',
  sourceSlices: {
    horizontal: { left: panelSlice, right: panelSlice },
  },
};

const tweenDuration = 0.15;

type ToggleSwitcherOpts = ViewOpts & {
  content?: { active: string; inactive: string };
  onToggle: (value: boolean) => void;
  tracking: {
    feature: string;
    $subFeature: string;
  };
  isOn?: boolean;
};

export class ToggleSwitcher extends ButtonScaleView {
  protected readonly overlay: FlexBoxImageScaleView;
  protected readonly label?: LangBitmapFontTextShadowView;
  protected readonly switch: FlexBoxImageScaleView;
  public onToggle: (value: boolean) => void;
  protected readonly content?: { active: string; inactive: string };
  protected _isOn = false;

  public get isOn(): boolean {
    return this._isOn;
  }

  private set isOn(value: boolean) {
    this._isOn = value;
    const key = value ? 'active' : 'inactive';

    // Update the panel and animation overlay image
    this.setImage(assets[key]);
    this.overlay.image.setImage(assets[value ? 'inactive' : 'active']);

    // Update label color and text, if label exists
    if (this.label) {
      this.label.updateOpts({
        color: labelColor[key],
        localeText: () => i18n(this.content?.[key] ?? ''),
      });
      this.label.updateShadow({
        textShadowColor: labelShadowColor[key],
      });
    }
  }

  private get labelPadding() {
    if (!this.label) return 0;
    return this.style.width - this.style.height - this.label.style.width;
  }

  constructor(opts: ToggleSwitcherOpts) {
    const {
      width = baseDimensions.width,
      height = baseDimensions.height,
      content,
      onToggle,
      isOn = false,
    } = opts;

    const imgScale = height / panelAssetDimensions.height;

    super({
      width,
      height,
      ...panelConfig,
      imgScale,
      ...opts,
      onClick: async () => {
        const value = !this.isOn;
        this.setToggled(value);
        this.onToggle(value);
      },
    });

    const wrapper = new FlexBoxView({
      superview: this,
      flex: {
        width,
        height,
      },
    });

    this.overlay = new FlexBoxImageScaleView({
      superview: wrapper,
      ...panelConfig,
      imgScale,
      flex: {
        position: 'absolute',
        width: '100%',
        height: '100%',
      },
    });

    if (content) {
      this.label = new LangBitmapFontTextShadowView({
        superview: wrapper,
        width: width - height - baseLabelPadding * imgScale,
        height,
        font: 'Body',
        size: (height / baseDimensions.height) * baseLabelSize,
        align: 'center',
        verticalAlign: 'center',
        textShadowOffsetX: 0,
        textShadowOffsetY: -height * 0.06,
      });
    }

    this.switch = new FlexBoxImageScaleView({
      superview: wrapper,
      image: assets.switch,
      flex: {
        position: 'absolute',
        aspectRatio: switchAssetDimensions.width / switchAssetDimensions.height,
        top: -1,
        bottom: -2,
      },
    });

    this.content = content;
    this.onToggle = onToggle;
    this.setToggled(isOn, true);
  }

  public setToggled(value: boolean, instant = false) {
    this.isOn = value;
    this.animatePanel(instant);
    this.animateSwitch(value, instant);
    this.animateLabel(value, instant);
  }

  private async animatePanel(instant = false) {
    this.overlay.updateOpts({ visible: true, opacity: 1 });
    await gsap.to(this.overlay.style, {
      duration: instant ? 0 : tweenDuration,
      opacity: 0,
      ease: 'quad.inOut',
      overwrite: true,
    });
    this.overlay.updateOpts({ visible: false });
  }

  private async animateSwitch(value: boolean, instant = false) {
    return gsap.to(this.switch.style, {
      duration: instant ? 0 : tweenDuration,
      offsetX: value ? this.style.width - this.style.height : 0,
      ease: 'quad.out',
      overwrite: true,
    });
  }

  private async animateLabel(value: boolean, instant = false) {
    if (!this.label) return undefined;
    return gsap.fromTo(
      this.label.style,
      {
        offsetX: value
          ? this.labelPadding
          : this.style.width - this.label.style.width - this.labelPadding,
        offsetY: this.style.height * 0.1,
        opacity: 0,
      },
      {
        duration: instant ? 0 : tweenDuration,
        offsetY: 0,
        opacity: 1,
        ease: 'quad.in',
        overwrite: true,
      },
    );
  }
}
